import { LinearProgress, makeStyles } from '@material-ui/core';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { pick } from 'lodash';
import { memo, useEffect, useState } from 'react';
import { useExtendUsersnap } from 'src/hooks/useExtendUsersnap';
import NotFoundPage from 'src/pages/404';

import PackageDetailContent from '../components/PackageDetailContent';
import { QueryKeys } from '../constants';
import useRole from '../hooks/useRole';
import { useValuationSettings } from '../hooks/useValuationSettings';
import ApiService from '../modules/api-service';
import { DEFAULT_VALUATION_COUNTRY } from '../modules/data';
import { SourceRegisterPotentialDTO, SourceType, ValuationCountryCode } from '../modules/generated/api';
import { MAX_NUMBER_CHECKABLE } from '../modules/global-vars';
import { czSkPairMap } from '../modules/valuation-country-helpers';
import UserRole from '../types/UserRoles';

const useStyles = makeStyles({
  loadingBar: {
    width: '100%',
    marginTop: '8px',
  },
});

const STORAGE_KEY = 'gwscout/comparingValuationCountries';

type PackageDetailProps = {
  source: SourceType;
  packageId: string;
  isDetailPage?: boolean;
};

const PackageDetail = ({ source, packageId, isDetailPage }: PackageDetailProps) => {
  const classes = useStyles();
  const queryClient = useQueryClient();
  const { hasRole } = useRole();
  const isAdmin = hasRole(UserRole.ADMIN);
  const { valuationCountry: globalValuationCountry } = useValuationSettings();
  const currentValuationCountry = globalValuationCountry ?? DEFAULT_VALUATION_COUNTRY;
  const czSkPairedCountry = currentValuationCountry ? czSkPairMap[currentValuationCountry] : undefined;
  const isNotDe = currentValuationCountry !== ValuationCountryCode.De;

  useEffect(() => {
    if (isDetailPage) document.title = `ALLUC - Package ${packageId}`;
  }, [isDetailPage, packageId]);

  const initialCountries = [
    ...new Set(
      [
        currentValuationCountry,
        ...(!isAdmin && czSkPairedCountry ? [czSkPairedCountry] : []),
        ...(!isAdmin && isNotDe ? [ValuationCountryCode.De] : []),
        globalValuationCountry,
        ...(Object.values(ValuationCountryCode) as ValuationCountryCode[]),
      ].filter((countryCode): countryCode is ValuationCountryCode => countryCode !== undefined && countryCode !== null),
    ),
  ].slice(0, MAX_NUMBER_CHECKABLE) as ValuationCountryCode[];

  const storedValuationCountries = localStorage.getItem(STORAGE_KEY);

  const storedCountries = storedValuationCountries
    ? (JSON.parse(storedValuationCountries) as ValuationCountryCode[])
    : initialCountries;

  // For non-admin users: force-include the CZ/SK paired country and DE regardless of stored preferences.
  const storedCountriesWithPair = isAdmin
    ? storedCountries
    : [
        ...new Set([
          ...(currentValuationCountry ? [currentValuationCountry] : []),
          ...(czSkPairedCountry ? [czSkPairedCountry] : []),
          ...(isNotDe ? [ValuationCountryCode.De] : []),
        ]),
      ];

  const [comparisonCountries, setComparisonCountries] = useState<Set<ValuationCountryCode>>(
    new Set(storedValuationCountries ? storedCountriesWithPair : initialCountries),
  );

  const countriesArray = Array.from(comparisonCountries);

  const response = useQueries({
    queries: countriesArray.map((country) => ({
      queryKey: [QueryKeys.packageDetail, source, packageId, country],
      queryFn: () =>
        ApiService.listCars.listCarsControllerPackageDetail(source, packageId, country).then((r) => r.data),
      enabled: !!source && !!packageId,
    })),
  });

  const packageData = response[0]?.data;
  const firstError = response[0]?.error;

  const countryPotentials = response.reduce<{ [key: string]: SourceRegisterPotentialDTO }>((acc, q, i) => {
    const { potential } = q.data ?? {};
    if (potential) acc[countriesArray[i]] = potential;
    return acc;
  }, {});

  const handleSetComparisonCountries = (countries: Set<ValuationCountryCode>) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...countries]));
    setComparisonCountries(countries);
  };

  const refetchAll = () => queryClient.invalidateQueries([QueryKeys.packageDetail, source, packageId]);

  const isLoading = response.some((q) => q.isLoading);

  useExtendUsersnap(
    {
      packageDetail: packageData ? pick(packageData, 'packageId', 'source') : undefined,
    },
    [packageData],
  );

  if (axios.isAxiosError(firstError) && [400, 404].includes(firstError.response?.status ?? 0)) {
    return <NotFoundPage />;
  }

  if (!packageData) {
    if (isLoading) return <LinearProgress className={classes.loadingBar} />;
    return null;
  }

  return (
    <PackageDetailContent
      packageDetails={packageData}
      comparisonCountries={comparisonCountries}
      setComparisonCountries={handleSetComparisonCountries}
      countryPotentials={countryPotentials}
      refresh={refetchAll}
    />
  );
};

export default memo(PackageDetail);
